import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Container } from '@/components/layout/Container'
import { AccountSidebar } from '@/components/account/AccountSidebar'
import { SignedInOnly } from '@/components/account/SignedInOnly'
import { Accordion } from '@/components/ui/Accordion'
import { Button } from '@/components/ui/Button'
import { LinkButton } from '@/components/ui/LinkButton'
import { Portal } from '@/components/ui/Portal'
import { useAuth } from '@/hooks/useAuth'
import {
  ackAccessWelcome,
  fetchAccessLoyalty,
  type AccessLoyalty,
} from '@/lib/api'
import { cn, formatPrice } from '@/lib/utils'
import { Check, Lock } from 'lucide-react'

const naira = (kobo: number) => formatPrice(kobo / 100)

export function LoyaltyPage() {
  const { refreshUser, isSignedIn, loading } = useAuth()
  const [loyalty, setLoyalty] = useState<AccessLoyalty | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [welcomeOpen, setWelcomeOpen] = useState(false)

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  useEffect(() => {
    if (loading || !isSignedIn) return
    let cancelled = false

    fetchAccessLoyalty()
      .then((data) => {
        if (cancelled || !data) return
        setLoyalty(data)
        setWelcomeOpen(data.showWelcome)
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })

    return () => {
      cancelled = true
    }
  }, [loading, isSignedIn])

  const dismissWelcome = useCallback(async () => {
    setWelcomeOpen(false)
    if (!loyalty) return
    await ackAccessWelcome(loyalty.tier)
    refreshUser()
  }, [loyalty, refreshUser])

  const accordionItems = [
    {
      id: 'what',
      title: 'What does this mean?',
      content: (
        <p className="text-sm leading-relaxed text-carbon/60">
          ACCESS is the Little Sky loyalty programme. Every captured order
          counts towards your tier — Bronze to start, then Silver, then Gold.
          Higher tiers unlock members-only rewards such as the private sale,
          early access to drops and a personal shopping assistant.
        </p>
      ),
    },
    {
      id: 'how',
      title: 'How do I move up a tier?',
      content: (
        <p className="text-sm leading-relaxed text-carbon/60">
          Reach either threshold for the next tier and you upgrade immediately:
          Silver at 2 orders or {naira(5_000_000)} lifetime spend, Gold at 5
          orders or {naira(15_000_000)} lifetime spend. Only captured payments
          count — pending or failed orders do not.
        </p>
      ),
    },
    {
      id: 'rewards',
      title: 'When do rewards unlock?',
      content: (
        <p className="text-sm leading-relaxed text-carbon/60">
          Each reward shows what it needs. Some unlock simply by holding a tier,
          others need a minimum number of orders — your welcome gift arrives with
          your first order. Locked rewards stay visible so you always know what
          is next.
        </p>
      ),
    },
  ]

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-6xl">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <AccountSidebar activePath="/account/loyalty" />

          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.2em] text-wd-orange">
              access
            </p>
            <h1 className="font-display text-4xl normal-case text-carbon md:text-5xl">
              {loyalty?.tierLabel ?? 'ACCESS'}
            </h1>

            <SignedInOnly>
              {loadError && (
                <p className="mt-8 text-sm text-carbon">
                  We could not load your ACCESS status. Please try again shortly.
                </p>
              )}

              {!loyalty && !loadError && (
                <p className="mt-8 text-sm text-carbon/60">Loading your ACCESS status...</p>
              )}

              {loyalty && (
                <>
                  {/* Progress dashboard */}
                  <section className="mt-10 border border-ink/10 bg-white p-6 sm:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-6">
                      <div>
                        <p className="text-xs uppercase tracking-[0.16em] text-carbon/50">
                          lifetime spend
                        </p>
                        <p className="font-display text-3xl text-carbon">
                          {naira(loyalty.lifetimeSpendKobo)}
                        </p>
                        <p className="mt-1 text-sm text-carbon/60">
                          {loyalty.capturedOrderCount} captured{' '}
                          {loyalty.capturedOrderCount === 1 ? 'order' : 'orders'}
                        </p>
                      </div>

                      <div className="text-right">
                        {loyalty.progress.nextTier ? (
                          <>
                            <p className="text-xs uppercase tracking-[0.16em] text-carbon/50">
                              to {loyalty.progress.nextTier}
                            </p>
                            <p className="font-display text-3xl text-carbon">
                              {naira(loyalty.progress.remainingSpendKobo)}
                            </p>
                            <p className="mt-1 text-sm text-carbon/60">
                              or {loyalty.progress.remainingOrders} more{' '}
                              {loyalty.progress.remainingOrders === 1 ? 'order' : 'orders'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs uppercase tracking-[0.16em] text-carbon">
                              top tier
                            </p>
                            <p className="font-display text-3xl text-carbon">
                              You&apos;re at Gold
                            </p>
                            <p className="mt-1 text-sm text-carbon/60">
                              Every ACCESS reward is unlocked
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 h-2 w-full bg-ink/10">
                      <div
                        className="h-2 bg-wd-orange transition-all duration-500"
                        style={{ width: `${loyalty.progress.percent}%` }}
                      />
                    </div>

                    <div className="mt-3 flex items-center justify-between text-xs uppercase tracking-[0.14em] text-carbon/50">
                      <span>{loyalty.tierLabel}</span>
                      <span>{loyalty.progress.percent}%</span>
                      {loyalty.progress.nextTier && (
                        <span>ACCESS {loyalty.progress.nextTier}</span>
                      )}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {loyalty.journey.map((step) => (
                        <span
                          key={step.tier}
                          className={cn(
                            'border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em]',
                            step.current
                              ? 'border-wd-orange bg-wd-orange text-white'
                              : 'border-ink/15 bg-mist text-carbon/50'
                          )}
                        >
                          {step.title}
                        </span>
                      ))}
                    </div>
                  </section>

                  {/* Rewards */}
                  <section className="mt-14">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-carbon">
                      Your rewards
                    </h2>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {loyalty.rewards.map((reward) => (
                        <div
                          key={reward.id}
                          className={cn(
                            'border p-5',
                            reward.unlocked
                              ? 'border-ink/15 bg-white'
                              : 'border-ink/10 bg-mist/70'
                          )}
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <span className="text-xs uppercase tracking-[0.16em] text-carbon/50">
                              {reward.tier}
                            </span>
                            {reward.unlocked ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-carbon">
                                <Check className="h-3.5 w-3.5" /> Unlocked
                              </span>
                            ) : (
                              <Lock className="h-3.5 w-3.5 text-carbon/40" />
                            )}
                          </div>
                          <p className="text-sm font-semibold uppercase tracking-[0.1em] text-carbon">
                            {reward.title}
                          </p>
                          <p
                            className={cn(
                              'mt-2 text-sm leading-relaxed',
                              reward.unlocked ? 'text-carbon/60' : 'text-carbon/40'
                            )}
                          >
                            {reward.blurb}
                          </p>
                          {!reward.unlocked && reward.unlockHint && (
                            <p className="mt-3 text-xs uppercase tracking-[0.12em] text-carbon">
                              {reward.unlockHint}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* My Journey carousel */}
                  <section className="mt-14" id="my-journey">
                    <div className="flex items-baseline justify-between gap-4">
                      <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-carbon">
                        My journey
                      </h2>
                      <p className="text-xs uppercase tracking-[0.14em] text-carbon/40">
                        scroll sideways
                      </p>
                    </div>

                    <div
                      className="journey-carousel mt-5 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-wd-orange"
                      tabIndex={0}
                      role="region"
                      aria-label="Membership tiers"
                    >
                      {loyalty.journey.map((step) => (
                        <article
                          key={step.tier}
                          className={cn(
                            'min-w-[260px] flex-1 snap-start border p-6',
                            step.current
                              ? 'border-ink bg-ink text-white'
                              : 'border-ink/10 bg-white'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <p
                              className={cn(
                                'text-xs font-semibold uppercase tracking-[0.16em]',
                                step.current ? 'text-white' : 'text-carbon/50'
                              )}
                            >
                              {step.title}
                            </p>
                            {step.current && (
                              <span className="text-xs uppercase tracking-[0.14em] text-white">
                                you are here
                              </span>
                            )}
                          </div>

                          <ul className="mt-4 space-y-3">
                            {step.benefits.map((benefit) => (
                              <li
                                key={benefit}
                                className={cn(
                                  'flex items-start gap-2 text-sm leading-snug',
                                  step.current ? 'text-white/85' : 'text-carbon/70'
                                )}
                              >
                                <Check
                                  className={cn(
                                    'mt-0.5 h-4 w-4 shrink-0',
                                    step.current ? 'text-wd-orange' : 'text-wd-orange/70'
                                  )}
                                />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </article>
                      ))}
                    </div>
                  </section>

                  <div className="mt-12 max-w-xl">
                    <Accordion items={accordionItems} />
                  </div>
                </>
              )}
            </SignedInOnly>
          </div>
        </div>
      </Container>

      {/* Welcome interstitial */}
      <Portal>
        <AnimatePresence>
          {welcomeOpen && loyalty && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-[80] bg-ink/40"
                onClick={dismissWelcome}
              />
              <div className="pointer-events-none fixed inset-0 z-[90] flex items-center justify-center p-4">
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  aria-label={`Welcome to ${loyalty.tierLabel}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 16 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="pointer-events-auto max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto border border-ink/10 bg-paper p-8 text-center shadow-2xl sm:p-10"
                >
                <p className="text-xs uppercase tracking-[0.24em] text-wd-orange">
                  access
                </p>
                <h2 className="mt-3 font-display text-3xl normal-case text-carbon md:text-4xl">
                  Welcome to {loyalty.tierLabel}
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-carbon/60">
                  {loyalty.tier === 'bronze'
                    ? 'Your membership is live. Every captured order moves you closer to Silver and its members-only rewards.'
                    : `Your ${loyalty.tier} membership is live — members-only rewards and journey benefits are ready to use.`}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <Button variant="secondary" onClick={dismissWelcome}>
                    Learn more
                  </Button>
                  <LinkButton to="/shop" onClick={dismissWelcome}>
                    Start shopping
                  </LinkButton>
                </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>
      </Portal>
    </div>
  )
}
